/**
 * Accessible single-select combobox (WAI-ARIA "select-only combobox" pattern).
 * Keeps a hidden native <select> in sync for `field.inputs[0].value`.
 */

import {
  optionDomId,
  createTrigger,
  createListbox,
  setActiveIndex as setActiveIndexShared,
  createOutsideClickHandler,
  setTriggerDisabled,
} from './dtpSelectShared';
import { createIcon } from '../../../icons';

let instanceCounter = 0;

export function createDtpSelect({
  id,
  options = [],
  placeholder = 'Select',
  selectedId = null,
  labelId,
} = {}) {
  const namespace = id || `dtp-select-${++instanceCounter}`;

  const container = document.createElement('div');
  container.className = 'dtp-select';

  // Hidden native <select> keeps field.inputs[0].value working.
  const nativeSelect = document.createElement('select');
  nativeSelect.className = 'dtp-select__native';
  nativeSelect.id = namespace;
  nativeSelect.tabIndex = -1;
  nativeSelect.setAttribute('aria-hidden', 'true');

  const placeholderOption = document.createElement('option');
  placeholderOption.value = '';
  placeholderOption.textContent = placeholder;
  nativeSelect.appendChild(placeholderOption);

  options.forEach((option) => {
    const optionEl = document.createElement('option');
    optionEl.value = option.id;
    optionEl.textContent = option.label;
    optionEl.setAttribute('data-valuecode', option.id);
    nativeSelect.appendChild(optionEl);
  });
  nativeSelect.value = selectedId || '';

  const { trigger, valueLabel } = createTrigger({ namespace, labelId });
  const listbox = createListbox({ namespace, labelId });

  const optionElements = [];

  options.forEach((option) => {
    const optionEl = document.createElement('div');
    optionEl.className = 'dtp-select__option';
    optionEl.id = optionDomId(namespace, option.id);
    optionEl.setAttribute('role', 'option');
    optionEl.setAttribute('data-value', option.id);

    const optionLabel = document.createElement('span');
    optionLabel.className = 'dtp-select__option-label';
    optionLabel.textContent = option.label;
    optionEl.appendChild(optionLabel);

    const optionCheck = createIcon('check', 'dtp-select__option-check');
    optionEl.appendChild(optionCheck);

    listbox.appendChild(optionEl);
    optionElements.push(optionEl);
  });

  container.appendChild(trigger);
  container.appendChild(listbox);
  container.appendChild(nativeSelect);

  let activeIndex = -1;
  let isOpen = false;

  function updateSelectedState() {
    const currentValue = nativeSelect.value;
    const selectedOption = options.find((option) => option.id === currentValue);
    valueLabel.textContent = selectedOption ? selectedOption.label : placeholder;
    valueLabel.classList.toggle('dtp-select__value--placeholder', !selectedOption);

    optionElements.forEach((optionEl) => {
      const isSelected = optionEl.getAttribute('data-value') === currentValue;
      optionEl.setAttribute('aria-selected', String(isSelected));
      optionEl.classList.toggle('dtp-select__option--selected', isSelected);
    });
  }

  function setActiveIndex(index, { scrollIntoView = true } = {}) {
    activeIndex = index;
    setActiveIndexShared({ trigger, optionElements, index, scrollIntoView });
  }

  function openListbox() {
    if (isOpen) {
      return;
    }
    isOpen = true;
    outsideClick.enable();
    container.classList.add('dtp-select--open');
    trigger.setAttribute('aria-expanded', 'true');
    const selectedIndex = options.findIndex((option) => option.id === nativeSelect.value);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
  }

  function closeListbox({ restoreFocus = false } = {}) {
    if (!isOpen) {
      return;
    }
    isOpen = false;
    outsideClick.disable();
    container.classList.remove('dtp-select--open');
    trigger.setAttribute('aria-expanded', 'false');
    setActiveIndex(-1);
    if (restoreFocus) {
      trigger.focus();
    }
  }

  function selectOption(optionId) {
    if (nativeSelect.value !== optionId) {
      nativeSelect.value = optionId;
      updateSelectedState();
      nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
    closeListbox({ restoreFocus: true });
  }

  trigger.addEventListener('click', () => {
    if (trigger.disabled) {
      return;
    }
    if (isOpen) {
      closeListbox({ restoreFocus: true });
    } else {
      openListbox();
    }
  });

  trigger.addEventListener('keydown', (e) => {
    if (trigger.disabled) {
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
      case 'Down':
        e.preventDefault();
        e.stopPropagation(); // avoid bubbling to page-level listeners (e.g. megamenu)
        if (!isOpen) {
          openListbox();
        } else {
          setActiveIndex(activeIndex + 1 >= optionElements.length ? 0 : activeIndex + 1);
        }
        break;
      case 'ArrowUp':
      case 'Up':
        e.preventDefault();
        e.stopPropagation();
        if (!isOpen) {
          openListbox();
        } else {
          setActiveIndex(activeIndex - 1 < 0 ? optionElements.length - 1 : activeIndex - 1);
        }
        break;
      case 'Home':
        if (isOpen) {
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (isOpen) {
          e.preventDefault();
          e.stopPropagation();
          setActiveIndex(optionElements.length - 1);
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        e.stopPropagation();
        if (isOpen && activeIndex >= 0) {
          selectOption(optionElements[activeIndex].getAttribute('data-value'));
        } else if (!isOpen) {
          openListbox();
        }
        break;
      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          e.stopPropagation();
          closeListbox({ restoreFocus: true });
        }
        break;
      case 'Tab':
        closeListbox({ restoreFocus: false });
        break;
      default:
        break;
    }
  });

  optionElements.forEach((optionEl, index) => {
    optionEl.addEventListener('mouseenter', () => setActiveIndex(index, { scrollIntoView: false }));
    optionEl.addEventListener('click', () => selectOption(optionEl.getAttribute('data-value')));
  });

  const outsideClick = createOutsideClickHandler(container, closeListbox);

  updateSelectedState();

  return {
    container,
    nativeSelect,
    triggerElement: trigger,
    setDisabled(disabled) {
      setTriggerDisabled(container, trigger, disabled);
    },
    destroy() {
      outsideClick.disable();
    },
  };
}
